// DataSplitterNodeModal.js
import React, { useState } from 'react';

import Modal from '../base/Modal';

const DataSplitterNodeModal = ({
  mode,
  nodeData,
  onSave,
  onDelete,
  onClose,
  viewOnly,
}) => {
  const [purpose, setPurpose] = useState(nodeData?.data?.purpose || '');
  const [levels, setLevels] = useState(
    nodeData?.data?.levels || [{ id: 0, description: '' }],
  );

  // Add a new level (child)
  const addLevel = () => {
    setLevels([...levels, { id: Date.now(), description: '' }]);
  };

  // Delete a level (cannot delete level 0)
  const deleteLevel = (id) => {
    if (levels[0].id === id) return; // Level 0 is fixed.
    setLevels(levels.filter((level) => level.id !== id));
  };

  // Update the description for a level.
  const updateLevelDescription = (id, newDesc) => {
    setLevels(
      levels.map((level) =>
        level.id === id ? { ...level, description: newDesc } : level,
      ),
    );
  };

  // Render a complete binary tree based on the number of levels.
  // For each level i, we render 2^i boxes (all sharing the same description).
  const renderCompleteBinaryTree = () => {
    return (
      <div className="flex flex-col items-center space-y-4">
        {levels.map((level, index) => {
          const count = Math.pow(2, index);
          const boxes = [];
          for (let i = 0; i < count; i++) {
            boxes.push(
              <div
                key={i}
                className="flex h-10 w-16 items-center justify-center rounded border border-zinc-500 bg-zinc-700 text-center text-xs"
              >
                {level.description ? level.description : `L${index}`}
              </div>,
            );
          }
          return (
            <div key={index} className="flex w-full justify-center space-x-2">
              {boxes}
            </div>
          );
        })}
      </div>
    );
  };
  //   const renderLevelVisualization = () => {
  //     return (
  //       <div className="flex items-center mt-4">
  //         {levels.map((level, index) => (
  //           <React.Fragment key={level.id}>
  //             <div className="p-2 bg-zinc-700 text-zinc-100 rounded shadow-md">
  //               Level {index}:{" "}
  //               {level.description ? (
  //                 level.description
  //               ) : (
  //                 <span className="text-zinc-400 italic">No description</span>
  //               )}
  //             </div>
  //             {index < levels.length - 1 && (
  //               <span className="mx-2 text-zinc-400">→</span>
  //             )}
  //           </React.Fragment>
  //         ))}
  //       </div>
  //     );
  //   };

  const handleSave = () => {
    const updatedNode = {
      ...nodeData,
      data: {
        purpose,
        levels,
      },
    };
    onSave(updatedNode);
  };

  return (
    <Modal
      title={
        viewOnly
          ? 'Data Splitter Node'
          : mode === 'add'
            ? 'Add Data Splitter Node'
            : 'Edit Data Splitter Node'
      }
      isOpen={true}
      onClose={onClose}
    >
      <div className="relative flex overflow-auto rounded bg-zinc-800 p-6 text-zinc-100 shadow-lg">
        {/* Left side: Form */}
        <div className="flex-1 pr-4">
          {/* Purpose Input */}
          <div className="mb-4">
            <label htmlFor="purpose" className="mb-1 block text-sm font-medium">
              Purpose
            </label>
            <textarea
              className="w-full rounded border border-zinc-600 bg-zinc-700 p-2 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
              placeholder="Define the purpose of this data splitter. What will the split data be used for?"
              value={purpose}
              disabled={viewOnly}
              onChange={(e) => setPurpose(e.target.value)}
              rows={3}
            />
            <p className="mt-1 text-xs text-zinc-400">
              This defines what the split data will be used for.
            </p>
          </div>

          {/* Data Format / Hierarchy */}
          <div className="mb-4">
            <label
              htmlFor="data-format"
              className="mb-1 block text-sm font-medium"
            >
              Data Format
            </label>
            <p className="mb-2 text-xs text-zinc-400">
              Define hierarchical levels for the data. Level 0 represents data
              common to all parts. Subsequent levels define data shared by
              subsets; the final level will be unique for each generated part.
            </p>
            {levels.map((level, index) => (
              <div key={level.id} className="mb-2 flex items-center">
                <div className="flex-1">
                  <input
                    type="text"
                    className="w-full rounded border border-zinc-600 bg-zinc-700 p-2 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
                    placeholder={`Data Description (Level ${index})`}
                    value={level.description}
                    disabled={viewOnly}
                    onChange={(e) =>
                      updateLevelDescription(level.id, e.target.value)
                    }
                  />
                </div>
                {index > 0 && !viewOnly && (
                  <button
                    className="ml-2 text-red-400 hover:text-red-600"
                    onClick={() => deleteLevel(level.id)}
                  >
                    Delete
                  </button>
                )}
              </div>
            ))}
            {!viewOnly && (
              <button
                className="mt-2 rounded bg-[#5652CF] px-4 py-2 text-white hover:bg-blue-600"
                onClick={addLevel}
              >
                + Add Level
              </button>
            )}
            {/* {renderLevelVisualization()} */}
          </div>

          {/* Action Buttons */}
          {!viewOnly && (
            <div className="mt-4 flex justify-end gap-3">
              {mode === 'edit' && (
                <button
                  className="rounded bg-zinc-700 px-4 py-2 text-zinc-300 hover:bg-zinc-600"
                  onClick={() => onDelete(nodeData.id)}
                >
                  Delete Node
                </button>
              )}
              <button
                className="rounded bg-zinc-700 px-4 py-2 text-zinc-300 hover:bg-zinc-600"
                onClick={handleSave}
              >
                {mode === 'add' ? 'Add Node' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>

        {/* Right side: Complete Binary Tree Visualization */}
        <div className="relative w-1/3">
          <div className="absolute inset-0 flex flex-col rounded bg-black bg-opacity-30 p-4">
            <h4 className="mb-2 text-center text-sm font-bold text-zinc-100">
              Hierarchical View
            </h4>
            {renderCompleteBinaryTree()}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DataSplitterNodeModal;
